import {
  mesmoDia,
  rotuloDoDia,
  TAMANHO_MAXIMO_ANEXO,
  TIPOS_ANEXO_ACEITOS,
  type CanalDetalhe,
  type CanalResumo,
  type ContatoInterno,
  type MensagemCanalChat,
  fraseDeDigitando,
  MOTIVOS_PAUSA,
  soAtende,
  podeGerirGrupo,
  PRAZOS_DE_FIXACAO,
  type PrazoDeFixacao,
  type ResultadoBuscaInterna,
  type SecaoDeConversas,
  type ParticipanteCanal,
} from '@mymaxx/shared';
import {
  Anexo,
  arquivoDeAudio,
  Citacao,
  Aviso,
  BarraGravacao,
  Botao,
  BotaoMicrofone,
  ImagemColada,
  imagemDaColagem,
  Modal,
  useRolagemDoFim,
  useGravadorAudio,
} from '@mymaxx/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  AtSign,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  CornerUpRight,
  ChevronDown,
  ChevronRight,
  Download,
  Folder,
  FolderMinus,
  FolderPlus,
  EyeOff,
  Hash,
  Info,
  MessageSquarePlus,
  MoreVertical,
  Paperclip,
  Pencil,
  Phone,
  PhoneOff,
  Pin,
  PinOff,
  Reply,
  Search,
  Send,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { useDiscar } from '../lib/discar';
import { internoApi } from '../lib/interno-api';
import {
  useAvisarQueDigito,
  useCanalAberto,
  useEventosInternos,
  useQuemDigita,
} from '../lib/interno-tempo-real';
import { useSessao } from '../lib/sessao';
import { EstrelaDaMensagem, PainelDaConversa } from './PainelDaConversa';

/**
 * A conversa interna da empresa (D20): a equipe entre si e com o técnico de campo.
 *
 * Três colunas na cabeça de quem usa, duas na tela: a lista do lado esquerdo e o fio do
 * lado direito, como em qualquer mensageiro. O desenho é deliberadamente parecido com o do
 * atendimento — quem já trabalha aqui não precisa aprender outra tela.
 *
 * O que NÃO tem, e é de propósito: fila, protocolo, SLA e avaliação. Isto não é
 * atendimento; é a conversa que antes acontecia no Telegram, fora do alcance da empresa.
 */
export function Interno() {
  const qc = useQueryClient();
  const [parametros, definirParametros] = useSearchParams();
  const canalAberto = parametros.get('canal');
  const [novaConversa, setNovaConversa] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [procurado, setProcurado] = useState('');
  /** O termo que de fato vai ao servidor — segurado para não buscar a cada tecla. */
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setTermo(procurado.trim()), 300);
    return () => clearTimeout(id);
  }, [procurado]);

  const canais = useQuery({ queryKey: ['interno-canais'], queryFn: internoApi.canais });
  const secoes = useQuery({ queryKey: ['interno-secoes'], queryFn: internoApi.secoes });
  const busca = useQuery({
    queryKey: ['interno-busca', termo],
    queryFn: () => internoApi.buscar(termo),
    enabled: termo.length >= 2,
  });
  const canal = useQuery({
    queryKey: ['interno-canal', canalAberto],
    queryFn: () => internoApi.detalhe(canalAberto!),
    enabled: !!canalAberto,
  });

  const abrir = (id: string | null) => {
    definirParametros(id ? { canal: id } : {}, { replace: true });
  };

  const recarregarLista = () => void qc.invalidateQueries({ queryKey: ['interno-canais'] });

  /**
   * Em que filtro a lista está. "Todos" é o padrão porque é o que quase sempre se quer;
   * os outros existem para quando a lista passou de sessenta conversas.
   */
  const [filtro, setFiltro] = useState<Filtro>('todos');
  /** Olhando o arquivo, em vez da lista. */
  const [vendoArquivadas, setVendoArquivadas] = useState(false);

  // A moldura é quem mantém o socket e quem toca o som; aqui só se cuida da tela aberta.
  // O som não toca na conversa que está na frente da pessoa: ela está vendo a mensagem
  // chegar — e quem decide isso é o canal registrado logo abaixo.
  const definirCanalAberto = useCanalAberto((e) => e.definir);
  useEffect(() => {
    definirCanalAberto(canalAberto);
    return () => definirCanalAberto(null);
  }, [canalAberto, definirCanalAberto]);

  const trocarMensagem = (canalId: string, mensagem: MensagemCanalChat, acrescentar: boolean) => {
    if (canalId !== canalAberto) return;
    qc.setQueryData<CanalDetalhe>(['interno-canal', canalAberto], (atual) => {
      if (!atual) return atual;
      const existe = atual.mensagens.some((m) => m.id === mensagem.id);
      if (existe) {
        return {
          ...atual,
          mensagens: atual.mensagens.map((m) => (m.id === mensagem.id ? mensagem : m)),
        };
      }
      return acrescentar ? { ...atual, mensagens: [...atual.mensagens, mensagem] } : atual;
    });
  };

  useEventosInternos({
    aoReceberMensagem: (e) => {
      trocarMensagem(e.canalId, e.mensagem, true);
      if (e.canalId === canalAberto) {
        // Chegou na conversa que está aberta: já está lida, e quem escreveu vê o "visto".
        void internoApi.marcarLido(e.canalId).then(recarregarLista);
      }
      recarregarLista();
    },
    aoMudarMensagem: (e) => trocarMensagem(e.canalId, e.mensagem, false),
    aoMudarCanal: () => {
      recarregarLista();
      if (canalAberto) void qc.invalidateQueries({ queryKey: ['interno-canal', canalAberto] });
    },
    aoLer: (e) => {
      if (e.canalId !== canalAberto) return;
      // Só a leitura do outro muda na tela; não vale recarregar o fio inteiro por isso.
      qc.setQueryData<CanalDetalhe>(['interno-canal', canalAberto], (atual) =>
        atual
          ? {
              ...atual,
              membros: atual.membros.map((m) =>
                m.tipo === e.quem.tipo && m.id === e.quem.id ? { ...m, lidoAte: e.lidoAte } : m,
              ),
            }
          : atual,
      );
    },
    aoMudarPresenca: (e) => {
      // A bolinha acende e apaga sem recarregar nada: a pessoa está na lista e no fio.
      qc.setQueryData<CanalResumo[]>(['interno-canais'], (atual) =>
        atual?.map((c) =>
          c.comQuem && c.comQuem.tipo === e.quem.tipo && c.comQuem.id === e.quem.id
            ? { ...c, comQuem: { ...c.comQuem, online: e.online, vistoEm: e.vistoEm } }
            : c,
        ),
      );
      if (!canalAberto) return;
      qc.setQueryData<CanalDetalhe>(['interno-canal', canalAberto], (atual) =>
        atual
          ? {
              ...atual,
              comQuem:
                atual.comQuem &&
                atual.comQuem.tipo === e.quem.tipo &&
                atual.comQuem.id === e.quem.id
                  ? { ...atual.comQuem, online: e.online, vistoEm: e.vistoEm }
                  : atual.comQuem,
              membros: atual.membros.map((m) =>
                m.tipo === e.quem.tipo && m.id === e.quem.id
                  ? { ...m, online: e.online, vistoEm: e.vistoEm }
                  : m,
              ),
            }
          : atual,
      );
    },
  });

  // Abrir um canal é lê-lo: o contador zera assim que a conversa aparece na tela.
  useEffect(() => {
    if (!canalAberto) return;
    void internoApi.marcarLido(canalAberto).then(() => {
      void qc.invalidateQueries({ queryKey: ['interno-canais'] });
    });
  }, [canalAberto, qc]);

  const todas = canais.data ?? [];
  const arquivadas = todas.filter((c) => c.arquivado);
  // A lista obedece ao filtro; o arquivo é uma vista à parte e ignora o filtro, porque
  // quem entra lá está procurando uma conversa específica, não arrumando a lista.
  const lista = vendoArquivadas
    ? arquivadas
    : todas.filter((c) => !c.arquivado && cabeNoFiltro(c, filtro));

  // Sair do arquivo quando ele esvazia: ficar numa tela vazia sem saber por quê é pior do
  // que voltar sozinho para a lista.
  useEffect(() => {
    if (vendoArquivadas && arquivadas.length === 0) setVendoArquivadas(false);
  }, [vendoArquivadas, arquivadas.length]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Conversas</h1>
          <p className="text-xs text-neutral-500">
            A conversa da equipe e do campo, dentro do MyMAXX. Atendimento de cliente fica na aba
            Atendimentos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MeuStatus />
          <Botao onClick={() => setNovaConversa(true)}>
            <MessageSquarePlus size={15} /> Nova conversa
          </Botao>
        </div>
      </header>

      {erro && (
        <div className="mb-2">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
        <div
          className={`${canalAberto ? 'hidden md:flex' : 'flex'} w-full flex-col border-r border-neutral-200 md:w-72 md:shrink-0 lg:w-80 dark:border-neutral-800`}
        >
          <div className="border-b border-neutral-200 p-2 dark:border-neutral-800">
            <label className="flex items-center gap-2 rounded-lg border border-neutral-300 px-2 dark:border-neutral-700">
              <Search size={15} className="shrink-0 text-neutral-400" />
              <input
                value={procurado}
                placeholder="Procurar mensagem, grupo ou pessoa"
                aria-label="Procurar nas conversas"
                onChange={(e) => setProcurado(e.target.value)}
                className="w-full bg-transparent py-1.5 text-sm outline-none"
              />
              {procurado && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  onClick={() => setProcurado('')}
                  className="shrink-0 text-neutral-400 hover:text-neutral-600"
                >
                  <X size={14} />
                </button>
              )}
            </label>

            {/* Os filtros, na ordem que a operação pediu. Só aparecem fora da busca e fora
              do arquivo: dentro deles, filtrar duas vezes confunde mais do que ajuda. */}
            {termo.length < 2 && !vendoArquivadas && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {FILTROS.map((op) => (
                  <button
                    key={op.id}
                    type="button"
                    onClick={() => setFiltro(op.id)}
                    className={`rounded-full px-3 py-1 text-xs transition ${
                      filtro === op.id
                        ? 'bg-marca-600 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                    }`}
                  >
                    {op.rotulo}
                    {op.id !== 'todos' &&
                      contarNoFiltro(todas, op.id) > 0 &&
                      ` ${contarNoFiltro(todas, op.id)}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* O arquivo mora acima da lista, como no WhatsApp: some quando está vazio, para
            não ocupar uma linha falando de nada. */}
          {termo.length < 2 && (arquivadas.length > 0 || vendoArquivadas) && (
            <button
              type="button"
              onClick={() => setVendoArquivadas((v) => !v)}
              className="flex w-full items-center gap-2 border-b border-neutral-200 px-3 py-2 text-left text-xs text-neutral-500 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
            >
              {vendoArquivadas ? <ArrowLeft size={14} /> : <Archive size={14} />}
              <span className="flex-1">
                {vendoArquivadas ? 'Voltar às conversas' : 'Arquivadas'}
              </span>
              {!vendoArquivadas && <span>{arquivadas.length}</span>}
            </button>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {termo.length >= 2 ? (
              // `todas`, e não a lista filtrada: é dela que sai o nome de uma conversa direta, e
              // um resultado de busca numa conversa escondida pelo filtro viraria "Conversa".
              <Resultados
                resultado={busca.data}
                carregando={busca.isPending}
                minhasConversas={todas}
                onAbrirCanal={(id) => abrir(id)}
                onFalarCom={async (contato) => {
                  const canal = await internoApi.abrirDireto(
                    contato.tipo === 'usuario'
                      ? { usuarioId: contato.id }
                      : { clienteId: contato.id },
                  );
                  setProcurado('');
                  recarregarLista();
                  abrir(canal.id);
                }}
              />
            ) : (
              <>
                {canais.isPending && <p className="p-4 text-sm text-neutral-500">Carregando…</p>}
                {canais.isSuccess && lista.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-neutral-500">
                    {vendoArquivadas
                      ? 'Nada arquivado.'
                      : filtro === 'todos'
                        ? 'Nenhuma conversa ainda. Comece uma com alguém da equipe ou com um técnico.'
                        : `Nada em "${FILTROS.find((o) => o.id === filtro)?.rotulo}".`}
                  </p>
                )}
                <ListaArrumada
                  canais={lista}
                  secoes={secoes.data ?? []}
                  canalAberto={canalAberto}
                  onAbrir={abrir}
                  onMudou={() => {
                    recarregarLista();
                    void qc.invalidateQueries({ queryKey: ['interno-secoes'] });
                  }}
                />
              </>
            )}
          </div>
        </div>

        {canal.data ? (
          <Fio
            canal={canal.data}
            onVoltar={() => abrir(null)}
            onErro={setErro}
            onMudou={() => {
              recarregarLista();
              void qc.invalidateQueries({ queryKey: ['interno-canal', canalAberto] });
              // O painel lateral lê de outra chave: sem isto, favoritar uma mensagem
              // gravava e a lista de favoritas continuava dizendo zero.
              void qc.invalidateQueries({ queryKey: ['interno-detalhes', canalAberto] });
            }}
          />
        ) : (
          <div className="hidden flex-1 items-center justify-center p-8 text-center text-sm text-neutral-500 md:flex">
            Escolha uma conversa à esquerda, ou comece uma nova.
          </div>
        )}
      </div>

      {novaConversa && (
        <NovaConversa
          onFechar={() => setNovaConversa(false)}
          onAberta={(id) => {
            setNovaConversa(false);
            recarregarLista();
            abrir(id);
          }}
        />
      )}
    </div>
  );
}

/**
 * O meu status, aqui no Conversas.
 *
 * Só para quem NÃO é "só atende": o atendente mexe nisso na estação, onde já mexe na pausa
 * — é o mesmo controle, e dois lugares para a mesma coisa seriam dois lugares para esquecer
 * de voltar (08/10).
 */
function MeuStatus() {
  const usuario = useSessao((e) => e.usuario);
  const definirUsuario = useSessao((e) => e.definirUsuario);
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  if (!usuario || soAtende(usuario.permissoes)) return null;

  const emPausa = usuario.statusPresenca === 'pausa';
  const rotulo =
    (emPausa ? (usuario.motivoPausa ?? 'Em pausa') : 'Disponível') +
    (usuario.invisivel ? ' · invisível' : '');

  const mudar = async (acao: () => Promise<unknown>, novo: Partial<typeof usuario>) => {
    setAberto(false);
    await acao();
    definirUsuario({ ...usuario, ...novo });
  };

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        className="flex items-center gap-2 rounded-lg border border-neutral-300 px-2.5 py-1.5 text-xs transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
      >
        {usuario.invisivel ? (
          <EyeOff size={12} className="shrink-0 text-neutral-400" />
        ) : (
          <span
            className={`size-2 shrink-0 rounded-full ${emPausa ? 'bg-amber-500' : 'bg-green-500'}`}
            aria-hidden
          />
        )}
        <span className="max-w-40 truncate">{rotulo}</span>
        <ChevronDown size={13} className="shrink-0 text-neutral-400" />
      </button>

      {aberto && (
        <ul
          role="listbox"
          // Ancorado à ESQUERDA do botão: à direita há a largura toda do painel, e à
          // esquerda a lista era cortada pela borda da área de conteúdo — sobravam só as
          // etiquetas "sem ligação", sem os nomes dos motivos (visto na tela, 08/10).
          className="absolute top-full left-0 z-30 mt-1 w-60 overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 text-xs shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!emPausa}
              onClick={() =>
                void mudar(() => atendimentoApi.definirPresenca('online'), {
                  statusPresenca: 'online',
                  motivoPausa: null,
                })
              }
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <span className="size-2 rounded-full bg-green-500" aria-hidden /> Disponível
            </button>
          </li>
          <li className="px-3 pt-2 pb-1 text-[10px] font-medium tracking-wide text-neutral-400 uppercase">
            Status
          </li>
          {MOTIVOS_PAUSA.map((m) => (
            <li key={m.rotulo}>
              <button
                type="button"
                role="option"
                aria-selected={emPausa && usuario.motivoPausa === m.rotulo}
                onClick={() =>
                  void mudar(() => atendimentoApi.definirPresenca('pausa', m.rotulo), {
                    statusPresenca: 'pausa',
                    motivoPausa: m.rotulo,
                  })
                }
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <span className="size-2 rounded-full bg-amber-500" aria-hidden />
                <span className="flex-1">{m.rotulo}</span>
                {m.barraLigacao && (
                  <span
                    title="Não recebe ligações"
                    className="flex items-center gap-1 text-[10px] text-neutral-400"
                  >
                    <PhoneOff size={11} /> sem ligação
                  </span>
                )}
              </button>
            </li>
          ))}
          <li className="mt-1 border-t border-neutral-200 pt-1 dark:border-neutral-700">
            <button
              type="button"
              onClick={() =>
                void mudar(() => atendimentoApi.definirInvisivel(!usuario.invisivel), {
                  invisivel: !usuario.invisivel,
                })
              }
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <EyeOff
                size={12}
                className={usuario.invisivel ? 'text-marca-600' : 'text-neutral-400'}
              />
              <span className="flex-1">
                {usuario.invisivel ? 'Voltar a aparecer' : 'Ficar invisível'}
              </span>
              <span className="text-[10px] text-neutral-400">ligação continua</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

type Filtro = 'todos' | 'grupos' | 'nao_lidos' | 'favoritos';

/**
 * Os filtros da lista, na ordem pedida pela operação (08/10).
 *
 * "Todos" vem primeiro e é o padrão: é o que se quer quase sempre. Os outros existem para
 * quem tem sessenta grupos e precisa achar um.
 */
const FILTROS: { id: Filtro; rotulo: string }[] = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'grupos', rotulo: 'Grupos' },
  { id: 'nao_lidos', rotulo: 'Não lidos' },
  { id: 'favoritos', rotulo: 'Favoritos' },
];

function cabeNoFiltro(canal: CanalResumo, filtro: Filtro): boolean {
  if (filtro === 'grupos') return canal.tipo === 'grupo';
  if (filtro === 'nao_lidos') return canal.naoLidas > 0;
  if (filtro === 'favoritos') return canal.favorito;
  return true;
}

/** Quantas conversas vivas cabem num filtro — é o número na pastilha. */
function contarNoFiltro(canais: CanalResumo[], filtro: Filtro): number {
  return canais.filter((c) => !c.arquivado && cabeNoFiltro(c, filtro)).length;
}

function LinhaCanal({
  canal,
  aberta,
  secoes,
  onAbrir,
  onMudou,
}: {
  canal: CanalResumo;
  aberta: boolean;
  secoes: SecaoDeConversas[];
  onAbrir: () => void;
  onMudou: () => void;
}) {
  const escrevendo = fraseDeDigitando(useQuemDigita(canal.id));

  return (
    // `group` para o menu só aparecer na linha sob o cursor: um botão em cada conversa
    // transformaria a lista num painel de controles.
    <div
      className={`group flex items-start gap-1 px-3 py-2.5 transition ${
        aberta ? 'bg-marca-50 dark:bg-marca-950' : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
      }`}
    >
      <button type="button" onClick={onAbrir} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          {canal.tipo === 'grupo' ? (
            <FotoDoGrupo url={canal.avatarUrl} />
          ) : (
            <Retrato pessoa={canal.comQuem} />
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{canal.nome}</span>
          {/* O dourado do favorito. Não muda a ordem da lista — fixar é que faz isso. */}
          {canal.favorito && <Star size={12} className="shrink-0 fill-amber-400 text-amber-500" />}
          {canal.fixado && <Pin size={12} className="shrink-0 text-neutral-400" />}
          {canal.silenciado && <BellOff size={12} className="shrink-0 text-neutral-400" />}
          {/* Alguém me chamou aqui: mencionou meu nome ou respondeu o que eu escrevi.
              Entre dez conversas com mensagem nova, é o que aponta a que fala comigo. */}
          {canal.chamado && (
            <AtSign size={13} className="shrink-0 text-marca-600 dark:text-marca-300" />
          )}
          {canal.naoLidas > 0 && (
            <span className="shrink-0 rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
              {canal.naoLidas}
            </span>
          )}
        </div>
        {/* Enquanto alguém escreve, o aviso toma o lugar da última mensagem — é a
          informação mais fresca que existe sobre esta conversa, e é o que o WhatsApp faz. */}
        <p
          className={`truncate pl-8 text-xs ${
            escrevendo ? 'text-marca-600 dark:text-marca-300' : 'text-neutral-500'
          }`}
        >
          {escrevendo ?? canal.ultimaMensagem ?? 'Sem mensagens'}
        </p>
      </button>
      <MenuDaConversa canal={canal} secoes={secoes} onMudou={onMudou} />
    </div>
  );
}

/**
 * O que dá para fazer com uma conversa sem abri-la: fixar, silenciar e guardar numa seção.
 *
 * Tudo aqui é da pessoa, não da conversa — o mesmo grupo pode estar fixado para quem é de
 * rotas e silenciado para quem não é.
 */
function MenuDaConversa({
  canal,
  secoes,
  onMudou,
}: {
  canal: CanalResumo;
  secoes: SecaoDeConversas[];
  onMudou: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  // Clicar fora fecha. Sem isto o menu fica pendurado na tela enquanto a pessoa navega.
  useEffect(() => {
    if (!aberto) return;
    const aoClicar = (e: MouseEvent) => {
      if (!caixa.current?.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoClicar);
    return () => document.removeEventListener('mousedown', aoClicar);
  }, [aberto]);

  const fazer = (acao: () => Promise<unknown>) => () => {
    setAberto(false);
    void acao().then(onMudou);
  };

  return (
    <div ref={caixa} className="relative shrink-0">
      <button
        type="button"
        aria-label="Opções da conversa"
        onClick={() => setAberto((v) => !v)}
        className={`rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700 ${
          aberto ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
        }`}
      >
        <MoreVertical size={15} />
      </button>

      {aberto && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-neutral-200 bg-white p-1 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
          <ItemDoMenu
            onClick={fazer(() => internoApi.fixar(canal.id, !canal.fixado))}
            icone={<Pin size={14} />}
          >
            {canal.fixado ? 'Desafixar' : 'Fixar no topo'}
          </ItemDoMenu>

          <ItemDoMenu
            onClick={fazer(() => internoApi.favoritarCanal(canal.id, !canal.favorito))}
            icone={
              <Star size={14} className={canal.favorito ? 'fill-amber-400 text-amber-500' : ''} />
            }
          >
            {canal.favorito ? 'Tirar dos favoritos' : 'Favoritar'}
          </ItemDoMenu>

          <ItemDoMenu
            onClick={fazer(() => internoApi.arquivar(canal.id, !canal.arquivado))}
            icone={canal.arquivado ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          >
            {canal.arquivado ? 'Tirar do arquivo' : 'Arquivar'}
          </ItemDoMenu>

          {canal.silenciado ? (
            <ItemDoMenu
              onClick={fazer(() => internoApi.tirarSilencio(canal.id))}
              icone={<Bell size={14} />}
            >
              Voltar a avisar
            </ItemDoMenu>
          ) : (
            <>
              <ItemDoMenu
                onClick={fazer(() => internoApi.silenciar(canal.id, 8))}
                icone={<BellOff size={14} />}
              >
                Silenciar por 8 horas
              </ItemDoMenu>
              <ItemDoMenu
                onClick={fazer(() => internoApi.silenciar(canal.id, null))}
                icone={<BellOff size={14} />}
              >
                Silenciar até eu desligar
              </ItemDoMenu>
            </>
          )}

          <div className="my-1 border-t border-neutral-200 dark:border-neutral-700" />
          <p className="px-2 py-1 text-[11px] text-neutral-500">Guardar em</p>
          {secoes.map((s) => (
            <ItemDoMenu
              key={s.id}
              onClick={fazer(() => internoApi.guardarEmSecao(canal.id, s.id))}
              icone={<Folder size={14} />}
            >
              {s.nome}
              {canal.secaoId === s.id && <Check size={13} className="ml-auto" />}
            </ItemDoMenu>
          ))}
          {canal.secaoId && (
            <ItemDoMenu
              onClick={fazer(() => internoApi.guardarEmSecao(canal.id, null))}
              icone={<FolderMinus size={14} />}
            >
              Tirar da seção
            </ItemDoMenu>
          )}
          <div className="my-1 border-t border-neutral-200 dark:border-neutral-700" />
          {/* Se a razão de sair do Telegram é ter controle do que foi tratado, poder
              extrair o histórico faz parte do ponto — e a extração fica registrada. */}
          <ItemDoMenu
            onClick={() => {
              setAberto(false);
              void internoApi
                .baixarHistorico(canal.id, canal.nome)
                .catch(() => alert('Não consegui baixar o histórico desta conversa.'));
            }}
            icone={<Download size={14} />}
          >
            Baixar histórico (CSV)
          </ItemDoMenu>

          <ItemDoMenu
            onClick={() => {
              const nome = prompt('Nome da nova seção (ex.: Rotas)');
              if (!nome?.trim()) return;
              setAberto(false);
              void internoApi
                .criarSecao(nome.trim())
                .then((s) => internoApi.guardarEmSecao(canal.id, s.id))
                .then(onMudou);
            }}
            icone={<FolderPlus size={14} />}
          >
            Nova seção…
          </ItemDoMenu>
        </div>
      )}
    </div>
  );
}

function ItemDoMenu({
  onClick,
  icone,
  children,
}: {
  onClick: () => void;
  icone: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
    >
      <span className="shrink-0 text-neutral-400">{icone}</span>
      {children}
    </button>
  );
}

/**
 * A lista arrumada: fixadas no topo, depois as seções, e por fim o que está solto.
 *
 * As seções abrem e fecham, e a que tem mensagem não lida nunca fica escondida — fechar
 * uma pasta não pode virar deixar de ver quem chamou.
 */
function ListaArrumada({
  canais,
  secoes,
  canalAberto,
  onAbrir,
  onMudou,
}: {
  canais: CanalResumo[];
  secoes: SecaoDeConversas[];
  canalAberto: string | null;
  onAbrir: (id: string) => void;
  onMudou: () => void;
}) {
  const [fechadas, setFechadas] = useState<Set<string>>(new Set());
  const fixadas = canais.filter((c) => c.fixado);
  const soltas = canais.filter((c) => !c.fixado && !c.secaoId);

  const linha = (c: CanalResumo) => (
    <li key={c.id}>
      <LinhaCanal
        canal={c}
        secoes={secoes}
        aberta={c.id === canalAberto}
        onAbrir={() => onAbrir(c.id)}
        onMudou={onMudou}
      />
    </li>
  );

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
      {fixadas.length > 0 && (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {fixadas.map(linha)}
        </ul>
      )}

      {secoes.map((s) => {
        const dentro = canais.filter((c) => !c.fixado && c.secaoId === s.id);
        if (dentro.length === 0) return null;
        const naoLidas = dentro.reduce((total, c) => total + c.naoLidas, 0);
        const fechada = fechadas.has(s.id) && naoLidas === 0;
        return (
          <div key={s.id}>
            <div className="flex items-center gap-1 px-2 py-1">
              <button
                type="button"
                onClick={() =>
                  setFechadas((atual) => {
                    const novo = new Set(atual);
                    if (novo.has(s.id)) novo.delete(s.id);
                    else novo.add(s.id);
                    return novo;
                  })
                }
                className="flex min-w-0 flex-1 items-center gap-1 text-left text-[11px] font-medium tracking-wide text-neutral-500 uppercase"
              >
                {fechada ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                <span className="truncate">{s.nome}</span>
                {naoLidas > 0 && (
                  <span className="ml-1 rounded-full bg-marca-600 px-1.5 text-[10px] text-white">
                    {naoLidas}
                  </span>
                )}
              </button>
              <button
                type="button"
                aria-label={`Apagar a seção ${s.nome}`}
                title="Apagar a seção (as conversas voltam para a lista)"
                onClick={() => {
                  if (!confirm(`Apagar a seção "${s.nome}"? As conversas voltam para a lista.`)) {
                    return;
                  }
                  void internoApi.apagarSecao(s.id).then(onMudou);
                }}
                className="shrink-0 rounded p-1 text-neutral-300 hover:text-neutral-600 dark:text-neutral-600 dark:hover:text-neutral-300"
              >
                <Trash2 size={12} />
              </button>
            </div>
            {!fechada && (
              <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {dentro.map(linha)}
              </ul>
            )}
          </div>
        );
      })}

      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">{soltas.map(linha)}</ul>
    </div>
  );
}

/**
 * O que a busca achou: conversas, pessoas e o que foi dito.
 *
 * Três listas e não uma: "qual era o nome daquele grupo?", "com quem eu falo sobre isso?" e
 * "onde foi que combinamos aquilo?" são perguntas diferentes, e misturá-las faria a
 * resposta de uma esconder a da outra.
 */
function Resultados({
  resultado,
  carregando,
  minhasConversas,
  onAbrirCanal,
  onFalarCom,
}: {
  resultado: ResultadoBuscaInterna | undefined;
  carregando: boolean;
  minhasConversas: CanalResumo[];
  onAbrirCanal: (canalId: string) => void;
  onFalarCom: (contato: ContatoInterno) => Promise<void>;
}) {
  // Conversa direta não tem nome próprio no banco: o nome dela é quem está do outro lado,
  // e quem sabe isso é esta tela, que sabe quem está lendo.
  const nomeDoCanal = (canalId: string, doServidor: string) =>
    doServidor || (minhasConversas.find((c) => c.id === canalId)?.nome ?? 'Conversa');
  if (carregando) return <p className="p-4 text-sm text-neutral-500">Procurando…</p>;
  const nada =
    !resultado ||
    (resultado.canais.length === 0 &&
      resultado.contatos.length === 0 &&
      resultado.mensagens.length === 0);
  if (nada) {
    return <p className="px-4 py-6 text-center text-sm text-neutral-500">Nada com esse termo.</p>;
  }

  return (
    <div className="divide-y divide-neutral-200 text-sm dark:divide-neutral-800">
      {resultado.canais.length > 0 && (
        <div className="py-1">
          <p className="px-3 py-1 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            Conversas
          </p>
          {resultado.canais.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onAbrirCanal(c.id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              {c.tipo === 'grupo' ? (
                <Hash size={14} className="shrink-0 text-neutral-400" />
              ) : (
                <Retrato pessoa={c.comQuem} />
              )}
              <span className="min-w-0 flex-1 truncate">{c.nome}</span>
            </button>
          ))}
        </div>
      )}

      {resultado.contatos.length > 0 && (
        <div className="py-1">
          <p className="px-3 py-1 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            Pessoas
          </p>
          {resultado.contatos.map((c) => (
            <button
              key={`${c.tipo}:${c.id}`}
              type="button"
              onClick={() => void onFalarCom(c)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <Retrato pessoa={c} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{c.nome}</span>
                <span className="block truncate text-xs text-neutral-500">{c.descricao}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {resultado.mensagens.length > 0 && (
        <div className="py-1">
          <p className="px-3 py-1 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            No que foi dito
          </p>
          {resultado.mensagens.map((m) => (
            <button
              key={m.mensagemId}
              type="button"
              onClick={() => onAbrirCanal(m.canalId)}
              className="block w-full px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <p className="truncate text-xs text-neutral-500">
                {nomeDoCanal(m.canalId, m.canalNome)} · {m.autorNome ?? 'Alguém'} ·{' '}
                {new Date(m.criadaEm).toLocaleDateString('pt-BR')}
              </p>
              <p className="line-clamp-2 text-sm">{m.trecho}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * O rosto de alguém, com a bolinha de presença no canto.
 *
 * Sem foto, as iniciais — e nunca um boneco genérico: numa lista de trinta pessoas, duas
 * letras distinguem e um ícone igual para todos não distingue nada.
 */
function Retrato({
  pessoa,
}: {
  pessoa?: (Presente & { nome?: string; avatarUrl?: string | null }) | null;
}) {
  const iniciais = (pessoa?.nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <span className="relative inline-block shrink-0" title={pessoa ? situacao(pessoa) : undefined}>
      {pessoa?.avatarUrl ? (
        <img src={pessoa.avatarUrl} alt="" className="size-7 rounded-full object-cover" />
      ) : (
        <span className="flex size-7 items-center justify-center rounded-full bg-neutral-200 text-[10px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">
          {iniciais}
        </span>
      )}
      {/* Quem está invisível não ganha bolinha nenhuma. Pintá-la de cinza diria "offline",
        que é uma afirmação sobre a pessoa — e não é o que ela pediu. */}
      {!pessoa?.oculto && (
        <span
          className={`absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-white dark:border-neutral-900 ${
            pessoa?.online
              ? pessoa.presenca === 'pausa'
                ? 'bg-amber-500'
                : 'bg-green-500'
              : 'bg-neutral-300 dark:bg-neutral-600'
          }`}
          aria-hidden="true"
        />
      )}
    </span>
  );
}

/**
 * O mínimo que a bolinha precisa saber de alguém — serve ao participante do canal e ao
 * contato da agenda, que não é a mesma coisa mas tem a mesma bolinha.
 */
type Presente = {
  online: boolean;
  vistoEm: string | null;
  presenca?: 'online' | 'pausa' | 'offline';
  /** Escolheu não mostrar presença: nem bolinha, nem "visto por último". */
  oculto?: boolean;
  /** O motivo da pausa, que é o status desta pessoa: "Reunião", "Almoço/Janta". */
  pausaAtual?: string | null;
};

/** "Disponível", "Em pausa" ou o visto por último, em uma linha. */
/** A foto do grupo na lista e no cabeçalho; sem foto, a cerquilha de sempre. */
function FotoDoGrupo({ url }: { url?: string | null }) {
  if (!url) return <Hash size={14} className="shrink-0 text-neutral-400" />;
  return <img src={url} alt="" className="size-6 shrink-0 rounded-full object-cover" />;
}

function situacao(pessoa: Presente): string {
  // Invisível não é offline: é SEM status. Dizer "fora do sistema" seria mentir para quem
  // olha, e dizer "visto às 14h" seria entregar justamente o que a pessoa desligou.
  if (pessoa.oculto) return '';
  // O motivo da pausa é o status: "Em reunião" diz muito mais do que "Em pausa", e é a
  // diferença entre insistir e esperar.
  if (pessoa.online && pessoa.pausaAtual) return `Em ${pessoa.pausaAtual.toLowerCase()}`;
  if (pessoa.online) return pessoa.presenca === 'pausa' ? 'Em pausa' : 'Disponível';
  if (!pessoa.vistoEm) return 'Nunca entrou';
  return `Visto ${desdeQuando(pessoa.vistoEm)}`;
}

/**
 * "agora há pouco", "há 12 min", "há 3 h", "ontem às 18:30", "em 02/10 às 09:14".
 *
 * Quanto mais velho, menos precisão — ninguém precisa saber que alguém esteve aqui há 437
 * minutos, e dizer "há 7 h" é o que a pessoa realmente quer saber.
 */
function desdeQuando(iso: string): string {
  const quando = new Date(iso);
  const minutos = Math.floor((Date.now() - quando.getTime()) / 60_000);
  if (minutos < 2) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;

  const hoje = new Date();
  const mesmoDia = quando.toDateString() === hoje.toDateString();
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (mesmoDia) return `há ${Math.floor(minutos / 60)} h`;

  const ontem = new Date(hoje.getTime() - 86_400_000);
  if (quando.toDateString() === ontem.toDateString()) return `ontem às ${hora}`;
  return `em ${quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${hora}`;
}

function Fio({
  canal,
  onVoltar,
  onErro,
  onMudou,
}: {
  canal: CanalDetalhe;
  onVoltar: () => void;
  onErro: (e: string | null) => void;
  onMudou: () => void;
}) {
  const [parametrosDoFio, definirParametrosDoFio] = useSearchParams();
  // O começo de frase que veio do atendimento ("Sobre o atendimento 2026…"): entra uma vez
  // no campo e sai da URL, para um F5 não escrever de novo por cima do que a pessoa digitou.
  const [texto, setTexto] = useState(() => parametrosDoFio.get('sobre') ?? '');
  useEffect(() => {
    if (!parametrosDoFio.get('sobre')) return;
    const limpo = new URLSearchParams(parametrosDoFio);
    limpo.delete('sobre');
    definirParametrosDoFio(limpo, { replace: true });
  }, [parametrosDoFio, definirParametrosDoFio]);

  const [enviando, setEnviando] = useState(false);
  const [verMembros, setVerMembros] = useState(false);
  const ligarPara = useDiscar((e) => e.ligarPara);
  // Vem da moldura, e não de um estado daqui: é a mesma fonte que alimenta a linha da
  // lista, e em grupo pode ser mais de uma pessoa ao mesmo tempo.
  const escrevendo = fraseDeDigitando(useQuemDigita(canal.id));

  /** Imagem colada esperando confirmação (ver `colar`). */
  const [colado, setColado] = useState<File | null>(null);
  /** A mensagem que está sendo respondida, se houver. */
  const [respondendo, setRespondendo] = useState<MensagemCanalChat | null>(null);
  /** A mensagem que está sendo corrigida. O texto dela vai para o campo de escrever. */
  const [editando, setEditando] = useState<MensagemCanalChat | null>(null);
  const meuId = useSessao((e) => e.usuario?.id);
  const podeGerirSetores = useSessao((e) => !!e.usuario?.permissoes.includes('setores.gerir'));
  // Na conversa de duas pessoas qualquer um dos dois fixa e solta; no grupo, o dono e os
  // admins. Num grupo de trinta, "qualquer um solta" quer dizer que ninguém mantém nada
  // no alto.
  const podeFixar =
    canal.tipo === 'direto' || (canal.doSetor ? podeGerirSetores : podeGerirGrupo(canal.meuPapel));

  const avisarQueDigito = useAvisarQueDigito();
  /** A mensagem escolhida para encaminhar, enquanto a pessoa escolhe o destino. */
  const [encaminhando, setEncaminhando] = useState<MensagemCanalChat | null>(null);
  /** O que está sendo digitado depois de um "@", para sugerir nomes. */
  const [mencionando, setMencionando] = useState<string | null>(null);

  /**
   * `Esc` desfaz uma camada por vez e, no fim, sai da conversa (08/10).
   *
   * Um `Esc` que fechasse a conversa direto perderia a citação que a pessoa acabou de
   * montar, ou a edição pela metade. A ordem é a do arrependimento: primeiro o que está no
   * meio do caminho, depois o painel lateral, por último a conversa inteira.
   */
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editando) return setEditando(null);
      if (respondendo) return setRespondendo(null);
      if (encaminhando) return setEncaminhando(null);
      if (verMembros) return setVerMembros(false);
      onVoltar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [editando, respondendo, encaminhando, verMembros, onVoltar]);

  const campo = useRef<HTMLTextAreaElement>(null);
  /** Quando o último aviso de "estou escrevendo" saiu — o volume é segurado aqui. */
  const ultimoAviso = useRef(0);
  const fim = useRef<HTMLDivElement>(null);
  const escolherArquivo = useRef<HTMLInputElement>(null);

  const gravador = useGravadorAudio({
    onErro: onErro,
    onGravado: (audio, mimeType) => void anexar(arquivoDeAudio(audio, mimeType)),
  });

  useRolagemDoFim(fim, `${canal.id}:${canal.mensagens.length}`);

  async function enviar() {
    // Gravando, o botão de enviar fecha o áudio; o envio segue pelo caminho do anexo
    // quando o gravador devolver o arquivo.
    if (gravador.gravando) {
      gravador.finalizar();
      return;
    }
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setEnviando(true);
    onErro(null);
    try {
      if (editando) await internoApi.editar(canal.id, editando.id, conteudo);
      else await internoApi.escrever(canal.id, conteudo, respondendo?.id);
      setTexto('');
      setRespondendo(null);
      setEditando(null);
      onMudou();
    } catch (e) {
      onErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar');
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Avisa que estou escrevendo, no máximo uma vez a cada três segundos.
   *
   * O aviso vale quatro segundos do outro lado, então um a cada três mantém o "digitando…"
   * aceso sem transformar cada tecla num pacote de rede.
   */
  function aoEscrever(valor: string) {
    setTexto(valor);
    // Um "@" com o que veio depois dele, e só quando está no fim do que foi digitado: no
    // meio da frase a pessoa já escolheu, e a lista só atrapalharia.
    const arroba = /@([\p{L}\p{N}._-]*)$/u.exec(valor);
    setMencionando(arroba ? (arroba[1] ?? '') : null);

    if (!valor.trim() || editando) return;
    if (Date.now() - ultimoAviso.current < 3000) return;
    ultimoAviso.current = Date.now();
    avisarQueDigito(canal.id);
  }

  /** Completa o "@" com o nome de quem foi escolhido na lista. */
  function escolherMencao(nome: string) {
    const primeiro = nome.split(/\s+/)[0] ?? nome;
    setTexto((atual) => atual.replace(/@([\p{L}\p{N}._-]*)$/u, `@${primeiro} `));
    setMencionando(null);
    campo.current?.focus();
  }

  /**
   * Leva o fio até a mensagem citada e a pisca.
   *
   * Piscar, e não só rolar: numa tela cheia de bolhas parecidas, chegar lá sem destaque
   * deixa a pessoa procurando qual era. A mensagem antiga demais para estar carregada
   * ainda não é alcançável — o fio traz as últimas 200 —, e nesse caso é melhor dizer do
   * que rolar para lugar nenhum.
   */
  function irParaMensagem(mensagemId: string) {
    const alvo = document.getElementById(`msg-${mensagemId}`);
    if (!alvo) {
      onErro('Essa mensagem é mais antiga do que o trecho carregado da conversa.');
      return;
    }
    onErro(null);
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alvo.classList.add('bg-marca-100', 'dark:bg-marca-900/50', 'rounded-lg');
    setTimeout(
      () => alvo.classList.remove('bg-marca-100', 'dark:bg-marca-900/50', 'rounded-lg'),
      1600,
    );
  }

  async function apagar(mensagem: MensagemCanalChat) {
    if (!confirm('Apagar esta mensagem? Quem está na conversa vai ver que ela foi apagada.')) {
      return;
    }
    try {
      await internoApi.apagar(canal.id, mensagem.id);
      if (editando?.id === mensagem.id) {
        setEditando(null);
        setTexto('');
      }
      onMudou();
    } catch (e) {
      onErro(e instanceof ErroApi ? e.mensagem : 'Não consegui apagar');
    }
  }

  /**
   * Anexo no canal interno: foto de campo, comprovante, recado de voz.
   *
   * Tamanho e tipo são conferidos aqui antes de pedir destino ao servidor — não por
   * confiança (o servidor confere de novo), mas para a pessoa saber na hora, em vez de
   * esperar o upload de um arquivo que vai ser recusado no fim.
   */
  async function anexar(file: File) {
    if (file.size > TAMANHO_MAXIMO_ANEXO) {
      onErro('Arquivo muito grande (máximo 20 MB).');
      return;
    }
    if (!TIPOS_ANEXO_ACEITOS.includes(file.type)) {
      onErro('Tipo de arquivo não aceito. Envie imagem, vídeo, áudio, PDF ou documento.');
      return;
    }
    setEnviando(true);
    onErro(null);
    try {
      await internoApi.enviarArquivo(canal.id, file);
      onMudou();
    } catch (e) {
      onErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar o arquivo');
    } finally {
      setEnviando(false);
      if (escolherArquivo.current) escolherArquivo.current.value = '';
    }
  }

  /**
   * Print colado com Ctrl+V — o caso mais comum aqui.
   *
   * O envio não é automático: a imagem aparece em miniatura e vai quando a pessoa
   * confirma. Num grupo da empresa, um Ctrl+V distraído é pior do que no atendimento.
   */
  function colar(evento: ClipboardEvent) {
    const imagem = imagemDaColagem(evento.clipboardData);
    if (!imagem) return;
    evento.preventDefault();
    setColado(imagem);
  }

  const presentes = canal.membros.filter((m) => !m.saiu);

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
          <button
            type="button"
            aria-label="Voltar para a lista"
            onClick={onVoltar}
            className="text-neutral-500 md:hidden"
          >
            <ArrowLeft size={18} />
          </button>
          {/* Clicar no nome abre os dados da conversa — o gesto que todo mundo já tem no
            dedo de outros mensageiros. */}
          <button
            type="button"
            onClick={() => setVerMembros((v) => !v)}
            className="min-w-0 flex-1 rounded px-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <p className="flex items-center gap-2 truncate text-sm font-medium">
              {canal.tipo === 'direto' ? (
                <Retrato pessoa={canal.comQuem} />
              ) : (
                <FotoDoGrupo url={canal.avatarUrl} />
              )}
              {canal.nome}
            </p>
            {/* Quem está escrevendo toma o lugar da situação enquanto dura: é a informação
              mais fresca que existe sobre a pessoa do outro lado. */}
            <p className="truncate text-xs text-neutral-500">
              {escrevendo
                ? escrevendo
                : canal.tipo === 'grupo'
                  ? `${presentes.length} participante(s)`
                  : canal.comQuem
                    ? situacao(canal.comQuem)
                    : ''}
            </p>
          </button>
          {/* Ligar só no 1:1: voz em grupo é outra conversa (mixagem, quem fala, quem
            entra no meio) e não é o que a operação precisava primeiro. */}
          {canal.tipo === 'direto' && (
            <button
              type="button"
              title={ligarPara ? 'Ligar por voz' : 'Sem conexão para ligar agora'}
              aria-label="Ligar por voz"
              disabled={!ligarPara}
              onClick={() => ligarPara?.(canal.id, canal.nome)}
              className="rounded-full p-2 text-neutral-500 hover:bg-neutral-100 hover:text-green-700 disabled:opacity-40 dark:hover:bg-neutral-800"
            >
              <Phone size={17} />
            </button>
          )}
        </div>

        <BarraFixadas
          canal={canal}
          podeSoltar={podeFixar}
          onIrPara={irParaMensagem}
          onMudou={onMudou}
        />

        <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-50 p-4 dark:bg-neutral-950">
          {/* Sem coluna estreita no meio: a bolha de quem recebe encosta à esquerda e a de
          quem envia à direita, como em qualquer mensageiro. Antes tudo se amontoava no
          centro com a tela larga sobrando dos dois lados (07/10). O limite de 75% da
          largura por bolha é o que impede uma linha de texto de um metro. */}
          <div className="w-full space-y-2">
            {canal.mensagens.length === 0 && (
              <p className="py-8 text-center text-sm text-neutral-500">
                Nada por aqui ainda. Escreva a primeira mensagem.
              </p>
            )}
            {canal.mensagens.map((m, i) => {
              const minha = m.autor?.tipo === 'usuario' && m.autor.id === meuId;
              // A faixa entra quando o dia muda em relação à mensagem anterior — e
              // antes da primeira, que sempre abre um dia.
              const anterior = canal.mensagens[i - 1];
              const abreODia = !anterior || !mesmoDia(anterior.criadaEm, m.criadaEm);
              return (
                <Fragment key={m.id}>
                  {abreODia && <FaixaDoDia quando={m.criadaEm} />}
                  <Bolha
                    key={m.id}
                    mensagem={m}
                    grupo={canal.tipo === 'grupo'}
                    minha={minha}
                    lida={minha && lidaPorTodos(m, canal.membros, meuId)}
                    podeApagar={minha || canal.meuPapel === 'dono'}
                    onResponder={() => {
                      setEditando(null);
                      setRespondendo(m);
                    }}
                    onEditar={
                      minha && m.tipo === 'texto'
                        ? () => {
                            setRespondendo(null);
                            setEditando(m);
                            setTexto(m.texto ?? '');
                          }
                        : undefined
                    }
                    onApagar={() => void apagar(m)}
                    onEncaminhar={() => setEncaminhando(m)}
                    onIrPara={irParaMensagem}
                    onFavoritar={onMudou}
                    canalId={canal.id}
                    podeFixar={podeFixar}
                  />
                </Fragment>
              );
            })}
            <div ref={fim} />
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void enviar();
          }}
          className="space-y-2 border-t border-neutral-200 p-3 dark:border-neutral-800"
        >
          {mencionando !== null && (
            <SugestoesDeMencao
              membros={canal.membros}
              comecando={mencionando}
              meuId={meuId}
              onEscolher={escolherMencao}
            />
          )}

          {respondendo && (
            <Citacao
              autor={respondendo.autor?.nome ?? 'Alguém'}
              resumo={respondendo.texto?.trim() || 'Anexo'}
              onCancelar={() => setRespondendo(null)}
              className="bg-black/5 dark:bg-white/10"
            />
          )}

          {editando && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
              <Pencil size={13} className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">Corrigindo uma mensagem enviada</span>
              <button
                type="button"
                onClick={() => {
                  setEditando(null);
                  setTexto('');
                }}
                className="shrink-0 underline"
              >
                cancelar
              </button>
            </div>
          )}

          {colado && (
            <ImagemColada
              arquivo={colado}
              enviando={enviando}
              onCancelar={() => setColado(null)}
              onEnviar={() => {
                const imagem = colado;
                setColado(null);
                void anexar(imagem);
              }}
            />
          )}

          <div className="flex items-end gap-2">
            <input
              ref={escolherArquivo}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void anexar(f);
              }}
            />
            {/* Gravando, a onda toma o lugar do campo e do clipe — e quem envia continua
              sendo o mesmo botão. */}
            {gravador.gravando ? (
              <BarraGravacao gravador={gravador} />
            ) : (
              <>
                <button
                  type="button"
                  aria-label="Anexar arquivo"
                  onClick={() => escolherArquivo.current?.click()}
                  className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
                >
                  <Paperclip size={18} />
                </button>
                <BotaoMicrofone gravador={gravador} desabilitado={enviando} />
                <textarea
                  ref={campo}
                  rows={2}
                  value={texto}
                  aria-label="Mensagem"
                  placeholder="Escreva para a equipe…"
                  onChange={(e) => aoEscrever(e.target.value)}
                  onPaste={colar}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void enviar();
                    }
                  }}
                  className="max-h-40 flex-1 resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
              </>
            )}
            <Botao
              type="submit"
              carregando={enviando}
              aria-label={gravador.gravando ? 'Enviar áudio' : 'Enviar'}
            >
              <Send size={16} />
            </Botao>
          </div>
        </form>

        {encaminhando && (
          <Encaminhar
            mensagem={encaminhando}
            deCanalId={canal.id}
            onFechar={() => setEncaminhando(null)}
            onErro={onErro}
          />
        )}
      </div>

      {verMembros && (
        <PainelDaConversa
          canal={canal}
          podeGerir={podeGerirGrupo(canal.meuPapel) || (canal.doSetor && podeGerirSetores)}
          onFechar={() => setVerMembros(false)}
          onMudou={onMudou}
          onIrPara={(id) => {
            setVerMembros(false);
            // No quadro seguinte: o painel sai da tela e o fio volta a ter a largura toda,
            // então a posição da mensagem muda bem na hora de rolar até ela.
            requestAnimationFrame(() => irParaMensagem(id));
          }}
        />
      )}
    </div>
  );
}

/**
 * Uma mensagem no fio interno.
 *
 * Quem decide o lado da bolha é esta tela, comparando o autor com quem está logado — e não
 * o servidor. A mensagem empurrada pelo WebSocket é montada uma vez só, com a identidade de
 * quem escreveu: um "minha" vindo pronto chegaria marcado como próprio para todo mundo.
 */
/**
 * A barra das mensagens fixadas, acima do fio.
 *
 * Mostra uma de cada vez e alterna no clique, como no WhatsApp: uma lista com cinco
 * recados empilhados tomaria metade da conversa justamente nos grupos em que ela é mais
 * necessária. O número à direita diz quantas existem, para ninguém achar que é só aquela.
 */
function BarraFixadas({
  canal,
  podeSoltar,
  onIrPara,
  onMudou,
}: {
  canal: CanalDetalhe;
  podeSoltar: boolean;
  onIrPara: (mensagemId: string) => void;
  onMudou: () => void;
}) {
  const [qual, setQual] = useState(0);
  const fixadas = canal.fixadas ?? [];
  if (fixadas.length === 0) return null;

  // A conversa pode ter perdido uma fixada enquanto a tela estava aberta.
  const indice = qual % fixadas.length;
  const atual = fixadas[indice]!;

  return (
    <div className="flex items-center gap-2 border-b border-neutral-200 bg-amber-50 px-3 py-1.5 dark:border-neutral-800 dark:bg-amber-950/30">
      <Pin size={13} className="shrink-0 text-amber-600 dark:text-amber-400" />
      <button
        type="button"
        onClick={() => {
          onIrPara(atual.id);
          // Com mais de uma, o próximo clique mostra a seguinte: é como se percorre as
          // fixadas sem precisar de uma lista aberta na tela.
          if (fixadas.length > 1) setQual((q) => q + 1);
        }}
        className="min-w-0 flex-1 text-left"
      >
        <span className="block truncate text-[11px] text-neutral-500">
          Fixada{atual.fixada?.porNome ? ` por ${atual.fixada.porNome}` : ''}
          {atual.fixada?.ate ? ` · até ${dataCurta(atual.fixada.ate)}` : ''}
        </span>
        <span className="block truncate text-xs">
          {atual.texto?.trim() || (atual.midia ? atual.midia.nomeOriginal : 'Anexo')}
        </span>
      </button>
      {fixadas.length > 1 && (
        <span className="shrink-0 text-[11px] text-neutral-500">
          {indice + 1}/{fixadas.length}
        </span>
      )}
      {podeSoltar && (
        <button
          type="button"
          title="Soltar do alto"
          aria-label="Soltar do alto"
          onClick={() => void internoApi.desafixarMensagem(canal.id, atual.id).then(onMudou)}
          className="shrink-0 rounded p-1 text-neutral-400 hover:bg-amber-100 hover:text-neutral-700 dark:hover:bg-amber-900"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

/** "12/10" ou "12/10/27" — o prazo da fixação dito curto, que é tudo o que cabe na barra. */
function dataCurta(iso: string): string {
  const d = new Date(iso);
  const esteAno = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    ...(esteAno ? {} : { year: '2-digit' }),
  });
}

/**
 * A faixa que separa os dias dentro da conversa.
 *
 * Grudada no topo enquanto o dia rola (`sticky`), como no WhatsApp: quem desce trinta
 * mensagens continua vendo de que dia são, em vez de ter que subir de volta para
 * descobrir.
 */
function FaixaDoDia({ quando }: { quando: string }) {
  return (
    <div className="sticky top-1 z-10 flex justify-center py-1">
      <span className="rounded-full bg-neutral-200/90 px-3 py-0.5 text-[11px] font-medium text-neutral-600 backdrop-blur dark:bg-neutral-800/90 dark:text-neutral-300">
        {rotuloDoDia(quando)}
      </span>
    </div>
  );
}

function Bolha({
  mensagem,
  grupo,
  minha,
  lida,
  podeApagar,
  onResponder,
  onEditar,
  onApagar,
  onEncaminhar,
  onIrPara,
  onFavoritar,
  canalId,
  podeFixar,
}: {
  mensagem: MensagemCanalChat;
  grupo: boolean;
  minha: boolean;
  lida: boolean;
  podeApagar: boolean;
  onResponder: () => void;
  onEditar?: () => void;
  onApagar: () => void;
  onEncaminhar: () => void;
  onIrPara: (mensagemId: string) => void;
  onFavoritar: () => void;
  canalId: string;
  podeFixar: boolean;
}) {
  if (mensagem.removida) {
    return (
      <p className="mx-auto max-w-[80%] rounded bg-neutral-200 px-2 py-1 text-center text-[11px] text-neutral-500 italic dark:bg-neutral-800">
        {mensagem.removidaPeloAutor
          ? 'Mensagem apagada pelo autor'
          : mensagem.removidaPorNome
            ? `Mensagem apagada por ${mensagem.removidaPorNome}`
            : 'Mensagem apagada'}
      </p>
    );
  }
  return (
    // `group` para as ações aparecerem no passar do mouse: três ícones fixos em cada
    // bolha transformariam o fio num painel de botões.
    <div
      id={`msg-${mensagem.id}`}
      className={`group flex items-end gap-1 scroll-mt-4 transition-colors ${
        minha ? 'flex-row-reverse' : ''
      }`}
    >
      {/* Chamada e aviso não são bolha: são o que aconteceu, e ficam no meio do fio como
        em qualquer mensageiro. Sem este desvio cairiam na regra do anexo ausente e
        apareceriam como "anexo removido pelo prazo de guarda". */}
      {mensagem.tipo === 'chamada' || mensagem.tipo === 'aviso' ? (
        <span className="mx-auto flex items-center gap-1.5 rounded-full bg-neutral-200 px-3 py-1 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {mensagem.tipo === 'chamada' ? <Phone size={11} /> : <Info size={11} />}
          {mensagem.texto}
          <span className="opacity-60">
            {new Date(mensagem.criadaEm).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </span>
      ) : (
        <div
          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line shadow-sm ${
            minha
              ? 'rounded-br-sm bg-marca-600 text-white'
              : 'rounded-bl-sm bg-white dark:bg-neutral-800'
          }`}
        >
          {/* No grupo, quem falou vem antes do que foi dito; no direto é sempre a mesma dupla. */}
          {grupo && !minha && (
            <p className="mb-0.5 text-[11px] font-medium opacity-70">{mensagem.autor?.nome}</p>
          )}
          {mensagem.encaminhada && (
            <p className="mb-0.5 flex items-center gap-1 text-[11px] italic opacity-70">
              <CornerUpRight size={11} /> encaminhada
            </p>
          )}
          {/* A mesma mensagem está na barra de cima: sem esta marca, quem rola a conversa
              não reconhece a que está fixada e procura de novo. */}
          {mensagem.fixada && (
            <p className="mb-0.5 flex items-center gap-1 text-[11px] opacity-70">
              <Pin size={11} /> fixada
            </p>
          )}
          {mensagem.respondeA && (
            // Dois níveis, como no WhatsApp: em cima quem escreveu a original, embaixo o que
            // ela dizia. E clicável: o clique leva até ela no fio, que é o movimento natural
            // de quem perde o contexto da resposta.
            <button
              type="button"
              onClick={() => onIrPara(mensagem.respondeA!.id)}
              className={`mb-1 block w-full rounded border-l-2 px-2 py-1 text-left text-[11px] opacity-90 transition hover:opacity-100 ${
                minha
                  ? 'border-white/60 bg-white/10'
                  : 'border-marca-500 bg-black/5 dark:bg-white/10'
              }`}
            >
              <span className="block font-medium">{mensagem.respondeA.autorNome ?? 'Alguém'}</span>
              <span className="block truncate opacity-80">{mensagem.respondeA.resumo}</span>
            </button>
          )}
          {mensagem.midia && (
            <Anexo
              url={mensagem.midia.url}
              nomeOriginal={mensagem.midia.nomeOriginal}
              mimeType={mensagem.midia.mimeType}
              tamanho={mensagem.midia.tamanho}
              previaUrl={mensagem.midia.previaUrl}
            />
          )}
          {/* O arquivo saiu pelo prazo de guarda e a mensagem ficou. Sem esta linha a bolha
            viraria um retângulo vazio, e quem lesse o fio não entenderia o que houve ali. */}
          {!mensagem.midia && mensagem.tipo !== 'texto' && (
            <p className="text-[11px] italic opacity-70">
              Anexo removido pelo prazo de guarda da conversa interna.
            </p>
          )}
          <TextoComMencoes texto={mensagem.texto} minha={minha} />
          <span className="ml-2 inline-flex items-center gap-1 align-bottom text-[10px] opacity-60">
            {mensagem.editadaEm && <span title="Mensagem editada">editada</span>}
            {new Date(mensagem.criadaEm).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            {/* O "visto" é só das minhas: saber que a MINHA mensagem foi lida é o que
              importa — o outro já sabe o que leu. */}
            {minha && <CheckCheck size={13} className={lida ? 'opacity-100' : 'opacity-40'} />}
          </span>
        </div>
      )}

      {/* Nada a responder, encaminhar ou guardar numa chamada nem num aviso do sistema. */}
      {mensagem.tipo !== 'chamada' && mensagem.tipo !== 'aviso' && (
        <div className="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          <AcaoDaBolha titulo="Responder" onClick={onResponder}>
            <Reply size={14} />
          </AcaoDaBolha>
          <AcaoDaBolha titulo="Encaminhar" onClick={onEncaminhar}>
            <CornerUpRight size={14} />
          </AcaoDaBolha>
          <EstrelaDaMensagem canalId={canalId} mensagem={mensagem} onMudou={onFavoritar} />
          {podeFixar && (
            <AlfineteDaMensagem canalId={canalId} mensagem={mensagem} onMudou={onFavoritar} />
          )}
          {onEditar && (
            <AcaoDaBolha titulo="Editar" onClick={onEditar}>
              <Pencil size={14} />
            </AcaoDaBolha>
          )}
          {podeApagar && (
            <AcaoDaBolha titulo="Apagar" onClick={onApagar}>
              <Trash2 size={14} />
            </AcaoDaBolha>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O alfinete da bolha: prende a mensagem no alto, com prazo.
 *
 * O prazo vem num menu curto em vez de um padrão escondido: fixar sem prazo é a escolha
 * que enche a barra de recados de três meses atrás, e ela deve ser uma escolha, não o que
 * acontece quando ninguém pensa no assunto.
 */
function AlfineteDaMensagem({
  canalId,
  mensagem,
  onMudou,
}: {
  canalId: string;
  mensagem: MensagemCanalChat;
  onMudou: () => void;
}) {
  const [escolhendo, setEscolhendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (mensagem.fixada) {
    return (
      <AcaoDaBolha
        titulo="Soltar do alto"
        onClick={() => void internoApi.desafixarMensagem(canalId, mensagem.id).then(onMudou)}
      >
        <PinOff size={14} className="text-amber-500" />
      </AcaoDaBolha>
    );
  }

  const fixar = (prazo: PrazoDeFixacao) => {
    setEscolhendo(false);
    setErro(null);
    void internoApi
      .fixarMensagem(canalId, mensagem.id, prazo)
      .then(onMudou)
      .catch((e: unknown) =>
        setErro(e instanceof Error ? e.message : 'Não consegui fixar esta mensagem.'),
      );
  };

  return (
    <>
      <AcaoDaBolha titulo="Fixar no alto" onClick={() => setEscolhendo(true)}>
        <Pin size={14} />
      </AcaoDaBolha>

      {/* Diálogo, e não menu suspenso: o fio rola, e um menu posicionado dentro dele era
        cortado pela borda da área de rolagem — o prazo "Sem prazo" ficava metade fora da
        tela (visto em verificação, 08/10). */}
      <Modal
        aberto={escolhendo}
        titulo="Fixar no alto por quanto tempo?"
        descricao="Passado o prazo, ela sai da barra sozinha."
        onFechar={() => setEscolhendo(false)}
      >
        <div className="grid gap-2">
          {PRAZOS_DE_FIXACAO.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => fixar(p.id)}
              className="rounded-lg border border-neutral-200 px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              {p.rotulo}
            </button>
          ))}
        </div>
      </Modal>

      {erro && (
        <Modal aberto titulo="Não deu" onFechar={() => setErro(null)}>
          <p className="text-sm">{erro}</p>
        </Modal>
      )}
    </>
  );
}

function AcaoDaBolha({
  titulo,
  onClick,
  children,
}: {
  titulo: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-100"
    >
      {children}
    </button>
  );
}

/**
 * A minha mensagem já foi lida por todo mundo que está no canal?
 *
 * Em grupo, o critério é "todos": dois tiques que acendem quando metade leu diriam uma
 * coisa que não é verdade. Quem saiu do grupo não conta — ele não vai ler mais nada.
 */
function lidaPorTodos(
  mensagem: MensagemCanalChat,
  membros: ParticipanteCanal[],
  meuId: string | undefined,
): boolean {
  const outros = membros.filter((m) => !m.saiu && !(m.tipo === 'usuario' && m.id === meuId));
  if (outros.length === 0) return false;
  const quando = new Date(mensagem.criadaEm).getTime();
  return outros.every((m) => !!m.lidoAte && new Date(m.lidoAte).getTime() >= quando);
}

/**
 * Começar uma conversa: com uma pessoa, ou um grupo com várias.
 *
 * A diferença entre as duas é só quantas pessoas são escolhidas — por isso a mesma tela
 * faz as duas, em vez de obrigar a decidir antes de ver quem existe.
 */
function NovaConversa({
  onFechar,
  onAberta,
}: {
  onFechar: () => void;
  onAberta: (canalId: string) => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const meuId = useSessao((e) => e.usuario?.id);

  return (
    <EscolherPessoas
      titulo="Nova conversa"
      excluir={meuId ? [`usuario:${meuId}`] : []}
      permitirGrupo
      erro={erro}
      onFechar={onFechar}
      onPronto={async (escolhidos, nomeDoGrupo) => {
        try {
          if (escolhidos.length === 1 && !nomeDoGrupo) {
            const um = escolhidos[0]!;
            const canal = await internoApi.abrirDireto(
              um.tipo === 'usuario' ? { usuarioId: um.id } : { clienteId: um.id },
            );
            onAberta(canal.id);
            return;
          }
          const canal = await internoApi.criarGrupo({
            nome: nomeDoGrupo?.trim() || 'Grupo',
            descricao: '',
            usuarioIds: escolhidos.filter((e) => e.tipo === 'usuario').map((e) => e.id),
            clienteIds: escolhidos.filter((e) => e.tipo === 'tecnico').map((e) => e.id),
          });
          onAberta(canal.id);
        } catch (e) {
          setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui abrir a conversa');
        }
      }}
    />
  );
}

function EscolherPessoas({
  titulo,
  jaDentro = [],
  excluir = [],
  permitirGrupo = false,
  erro,
  onFechar,
  onPronto,
}: {
  titulo: string;
  jaDentro?: string[];
  excluir?: string[];
  permitirGrupo?: boolean;
  erro?: string | null;
  onFechar: () => void;
  onPronto: (escolhidos: ContatoInterno[], nomeDoGrupo?: string) => Promise<void>;
}) {
  const [busca, setBusca] = useState('');
  const [escolhidos, setEscolhidos] = useState<ContatoInterno[]>([]);
  const [nomeGrupo, setNomeGrupo] = useState('');
  const [salvando, setSalvando] = useState(false);

  const contatos = useQuery({
    queryKey: ['interno-contatos', busca],
    queryFn: () => internoApi.contatos(busca || undefined),
  });

  const fora = useMemo(() => new Set([...jaDentro, ...excluir]), [jaDentro, excluir]);
  const lista = (contatos.data ?? []).filter((c) => !fora.has(`${c.tipo}:${c.id}`));
  const virouGrupo = permitirGrupo && escolhidos.length > 1;

  return (
    <Modal
      aberto
      titulo={titulo}
      onFechar={onFechar}
      acoes={
        <Botao
          carregando={salvando}
          disabled={escolhidos.length === 0 || (virouGrupo && !nomeGrupo.trim())}
          onClick={() => {
            setSalvando(true);
            void onPronto(escolhidos, virouGrupo ? nomeGrupo : undefined).finally(() =>
              setSalvando(false),
            );
          }}
        >
          {virouGrupo ? 'Criar grupo' : 'Conversar'}
        </Botao>
      }
    >
      <div className="space-y-3">
        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        <label className="flex items-center gap-2 rounded-lg border border-neutral-300 px-2 dark:border-neutral-700">
          <Search size={15} className="text-neutral-400" />
          <input
            autoFocus
            value={busca}
            placeholder="Procurar pessoa ou técnico"
            aria-label="Procurar pessoa"
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent py-2 text-sm outline-none"
          />
        </label>

        {/* Escolher mais de uma pessoa transforma a conversa em grupo, e grupo precisa de
            nome: é por ele que as pessoas o reconhecem na lista. */}
        {virouGrupo && (
          <input
            value={nomeGrupo}
            placeholder="Nome do grupo (ex.: Rotas e campo)"
            aria-label="Nome do grupo"
            onChange={(e) => setNomeGrupo(e.target.value)}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
        )}

        <ul className="max-h-[45vh] space-y-1 overflow-y-auto">
          {lista.map((c) => {
            const chave = `${c.tipo}:${c.id}`;
            const marcado = escolhidos.some((e) => `${e.tipo}:${e.id}` === chave);
            return (
              <li key={chave}>
                <button
                  type="button"
                  onClick={() =>
                    setEscolhidos((atual) =>
                      marcado ? atual.filter((e) => `${e.tipo}:${e.id}` !== chave) : [...atual, c],
                    )
                  }
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
                    marcado
                      ? 'border-marca-400 bg-marca-50 dark:border-marca-700 dark:bg-marca-950'
                      : 'border-neutral-200 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-900'
                  }`}
                >
                  <Retrato pessoa={c} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{c.nome}</span>
                    <span className="block truncate text-xs text-neutral-500">{c.descricao}</span>
                  </span>
                </button>
              </li>
            );
          })}
          {contatos.isSuccess && lista.length === 0 && (
            <li className="py-6 text-center text-sm text-neutral-500">Ninguém com esse nome.</li>
          )}
        </ul>
      </div>
    </Modal>
  );
}

/**
 * O texto da bolha, com os nomes chamados em destaque.
 *
 * Só destaca o que casa com alguém do canal? Não — destaca todo "@palavra". Conferir com a
 * lista de membros aqui daria uma segunda verdade sobre quem foi chamado, e a que vale é a
 * do servidor, que é quem decide o aviso. O destaque é leitura, não regra.
 */
function TextoComMencoes({ texto, minha }: { texto: string | null; minha: boolean }) {
  if (!texto) return null;
  const partes = texto.split(/(@[\p{L}\p{N}._-]{2,40})/gu);
  return (
    <>
      {partes.map((parte, i) =>
        parte.startsWith('@') ? (
          <span
            key={i}
            className={`rounded px-0.5 font-medium ${minha ? 'bg-white/25' : 'bg-marca-100 text-marca-800 dark:bg-marca-900 dark:text-marca-100'}`}
          >
            {parte}
          </span>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  );
}

/**
 * Quem dá para chamar, enquanto se digita depois do "@".
 *
 * Existe para a menção acertar o nome: o servidor casa pelo primeiro nome ou pelo nome
 * inteiro sem espaços, e quem digita de memória erra — "@jo" não chama ninguém.
 */
function SugestoesDeMencao({
  membros,
  comecando,
  meuId,
  onEscolher,
}: {
  membros: ParticipanteCanal[];
  comecando: string;
  meuId: string | undefined;
  onEscolher: (nome: string) => void;
}) {
  const alvo = comecando
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const candidatos = membros
    .filter((m) => !m.saiu && !(m.tipo === 'usuario' && m.id === meuId))
    .filter((m) =>
      m.nome
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .includes(alvo),
    )
    .slice(0, 6);
  if (candidatos.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-neutral-200 p-1 dark:border-neutral-700">
      {candidatos.map((m) => (
        <button
          key={`${m.tipo}:${m.id}`}
          type="button"
          onClick={() => onEscolher(m.nome)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <Retrato pessoa={m} />
          {m.nome}
        </button>
      ))}
    </div>
  );
}

/**
 * Para onde levar a mensagem.
 *
 * Duas listas, como no WhatsApp: as conversas que já existem e, abaixo, qualquer pessoa da
 * empresa — mesmo que eu nunca tenha falado com ela. Só as conversas existentes seria uma
 * limitação sem razão: a pessoa certa para receber aquela foto costuma ser justamente
 * alguém com quem ainda não se conversou (pedido de 07/10).
 *
 * Escolhendo alguém de fora, a conversa direta é aberta na hora e a mensagem cai nela.
 */
function Encaminhar({
  mensagem,
  deCanalId,
  onFechar,
  onErro,
}: {
  mensagem: MensagemCanalChat;
  deCanalId: string;
  onFechar: () => void;
  onErro: (e: string | null) => void;
}) {
  const [busca, setBusca] = useState('');
  const [enviando, setEnviando] = useState<string | null>(null);
  const meuId = useSessao((e) => e.usuario?.id);
  const canais = useQuery({ queryKey: ['interno-canais'], queryFn: internoApi.canais });
  const contatos = useQuery({
    queryKey: ['interno-contatos', busca],
    queryFn: () => internoApi.contatos(busca || undefined),
  });

  const alvo = semAcento(busca);
  const conversas = (canais.data ?? [])
    .filter((c) => c.id !== deCanalId)
    .filter((c) => !alvo || semAcento(c.nome).includes(alvo));

  // Quem já tem conversa direta não aparece duas vezes: a linha de cima já é ele.
  const jaTemConversa = new Set(
    (canais.data ?? []).filter((c) => c.comQuem).map((c) => `${c.comQuem!.tipo}:${c.comQuem!.id}`),
  );
  const pessoas = (contatos.data ?? []).filter(
    (c) => !jaTemConversa.has(`${c.tipo}:${c.id}`) && !(c.tipo === 'usuario' && c.id === meuId),
  );

  async function levar(paraCanalId: string, chave: string) {
    setEnviando(chave);
    try {
      await internoApi.encaminhar(deCanalId, mensagem.id, paraCanalId);
      onFechar();
    } catch (e) {
      onErro(e instanceof ErroApi ? e.mensagem : 'Não consegui encaminhar');
    } finally {
      setEnviando(null);
    }
  }

  return (
    <Modal aberto titulo="Encaminhar para" onFechar={onFechar}>
      <div className="space-y-3">
        <p className="rounded-lg bg-neutral-100 p-2 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {mensagem.texto?.slice(0, 160) || (mensagem.midia ? 'Anexo' : '')}
        </p>

        <label className="flex items-center gap-2 rounded-lg border border-neutral-300 px-2 dark:border-neutral-700">
          <Search size={15} className="text-neutral-400" />
          <input
            autoFocus
            value={busca}
            placeholder="Procurar conversa ou pessoa"
            aria-label="Procurar para onde encaminhar"
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent py-2 text-sm outline-none"
          />
        </label>

        <div className="max-h-[50vh] space-y-3 overflow-y-auto">
          {conversas.length > 0 && (
            <div>
              <p className="px-1 py-1 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
                Conversas recentes
              </p>
              <ul className="space-y-1">
                {conversas.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={!!enviando}
                      onClick={() => void levar(c.id, c.id)}
                      className="flex w-full items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-left text-sm hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
                    >
                      {c.tipo === 'grupo' ? (
                        <Hash size={14} className="shrink-0 text-neutral-400" />
                      ) : (
                        <Retrato pessoa={c.comQuem} />
                      )}
                      <span className="min-w-0 flex-1 truncate">{c.nome}</span>
                      {enviando === c.id && (
                        <span className="text-xs text-neutral-500">enviando…</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {pessoas.length > 0 && (
            <div>
              <p className="px-1 py-1 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
                Outras pessoas
              </p>
              <ul className="space-y-1">
                {pessoas.map((p) => {
                  const chave = `${p.tipo}:${p.id}`;
                  return (
                    <li key={chave}>
                      <button
                        type="button"
                        disabled={!!enviando}
                        onClick={() => {
                          setEnviando(chave);
                          void internoApi
                            .abrirDireto(
                              p.tipo === 'usuario' ? { usuarioId: p.id } : { clienteId: p.id },
                            )
                            .then((canal) => levar(canal.id, chave))
                            .catch((e: unknown) =>
                              onErro(
                                e instanceof ErroApi ? e.mensagem : 'Não consegui abrir a conversa',
                              ),
                            );
                        }}
                        className="flex w-full items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-left text-sm hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
                      >
                        <Retrato pessoa={p} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{p.nome}</span>
                          <span className="block truncate text-xs text-neutral-500">
                            {p.descricao}
                          </span>
                        </span>
                        {enviando === chave && (
                          <span className="text-xs text-neutral-500">enviando…</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {conversas.length === 0 && pessoas.length === 0 && (
            <p className="py-6 text-center text-sm text-neutral-500">
              {busca ? 'Ninguém com esse nome.' : 'Nenhuma conversa ou pessoa disponível.'}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Texto comparável: sem acento e em minúsculas, do jeito que as pessoas procuram. */
function semAcento(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}
